import { yup } from '@framework/validation';
import { fromPairs } from 'lodash';
import { ObjectsArrayFieldDatatype } from '../../schema/expressions/datatype/Datatype';
import { isExpressionReferenceOfObjectsArray } from '../../schema/expressions/datatype/isExpressionReferenceOfObjectsArray';
import { evaluateExpression } from '../../schema/expressions/evaluate/evaluateExpression';
import { mapReferenceToPath } from '../../schema/expressions/evaluate/mapReferenceToPath';
import { createEvaluationVariables } from '../../schema/expressions/evaluate/variables/IEvaluationVariables';
import { IReference } from '../../schema/expressions/references/IReference';
import { collectExpressionReferences } from '../../schema/expressions/references/collectExpressionReferences';
import { Widget } from '../../schema/widgets/Widget';

export function setWidgetDefaultValue<W extends Widget>(
  widget: W,
  schema: yup.AnySchema,
) {
  if (!('defaultValue' in widget)) return schema;
  if (widget.defaultValue == null) return schema;

  const expression = widget.defaultValue;

  const references = collectExpressionReferences(expression);
  const paths = references.map(r => mapReferenceToPath(r));

  return schema.parentWhen(
    paths,
    (values, schema: yup.AnySchema, context: any) => {
      const variables = createEvaluationVariables(references, values);
      const result = evaluateExpression(expression, { variables });

      if (result != null && isExpressionReferenceOfObjectsArray(expression)) {
        // Se il valore di default è un riferimento a un campo di tipo `objectsArray`
        // devo trasformarlo in base a quanto configurato nel widget
        // (vedi `defaultValueChildren`)
        if (!Array.isArray(result) || result.length === 0) return schema;

        if (widget.defaultValueChildren == null) {
          console.warn(`[setWidgetDefaultValue] Widget ${widget.code} has a default value of type objectsArray, but no defaultValueChildren are configured`); // prettier-ignore
        }

        const resultWithChildren = replaceValueWithChildrenMapping(
          result as any,
          expression.value.field,
          widget.defaultValueChildren!,
        );

        return schema.default(resultWithChildren);
      }

      // N.B. Utilizzare != null perché il valore può essere un boolean (`false`)
      return result != null ? schema.default(result) : schema;
    },
  );
}

function replaceValueWithChildrenMapping(
  items: Array<Record<string, any>>,
  field: ObjectsArrayFieldDatatype,
  childrenMapping: Array<IReference | null>,
) {
  const replaced = items.map(item => {
    return fromPairs(
      childrenMapping
        .map((child, index) => {
          console.log(`[${index} Provo a mappare ${child?.code ?? '<na>'} a ${field.structure[index]?.code ?? '<na>'}`); // prettier-ignore

          const valueCode = child?.code;
          const sourceCode = field.structure[index]?.code;

          if (!valueCode || !sourceCode) return null;

          return [valueCode, item[sourceCode]];
        })
        .filter((s): s is [string, any] => s != null),
    );
  });

  console.log(`[Mapping item]`, { items, replaced });
  return replaced;
}
